import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  MapPin, Phone, Globe, Clock, Star, BadgeCheck, Award, 
  Heart, Share2, Flag, ChevronRight, ExternalLink, Navigation,
  Calendar, MessageSquare, ThumbsUp, HelpCircle, Send, User,
  Mail, Building2, FileText, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import StarRating from '../components/common/StarRating';
import ListingMap from '../components/common/ListingMap';
import ListingCard from '../components/common/ListingCard';
import { listingsAPI, reviewsAPI, userAPI, analyticsAPI, questionsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [nearbyListings, setNearbyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(0);
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Claim dialog state
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimStep, setClaimStep] = useState(1);
  const [claimData, setClaimData] = useState({
    verification_method: 'admin',
    business_role: '',
    phone_verification: '',
    documentation_url: '',
    additional_notes: '',
  });
  const [submittingClaim, setSubmittingClaim] = useState(false);
  
  // Q&A state
  const [questionText, setQuestionText] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [answeringQuestionId, setAnsweringQuestionId] = useState(null);

  useEffect(() => {
    loadListing();
  }, [id]);

  const loadListing = async () => {
    setLoading(true);
    try {
      const res = await listingsAPI.getById(id);
      setListing(res.data);
      
      // Track view
      await analyticsAPI.track({ listing_id: res.data.listing_id, event_type: 'view' });
      
      // Load reviews
      const reviewsRes = await reviewsAPI.getAll({ listing_id: res.data.listing_id });
      setReviews(reviewsRes.data);
      
      // Load questions
      try {
        const questionsRes = await questionsAPI.getAll(res.data.listing_id);
        setQuestions(questionsRes.data);
      } catch (e) {
        console.error('Error loading questions:', e);
      }
      
      // Load nearby listings
      const nearbyRes = await listingsAPI.getAll({ 
        city: res.data.city, 
        listing_type: res.data.listing_type,
        limit: 4 
      });
      setNearbyListings(nearbyRes.data.filter(l => l.listing_id !== res.data.listing_id).slice(0, 3));
      
      // Check if favorited
      if (isAuthenticated) {
        try {
          const favRes = await userAPI.getFavorites();
          setIsFavorite(favRes.data.some(f => f.listing_id === res.data.listing_id));
        } catch (e) {}
      }
    } catch (error) {
      console.error('Error loading listing:', error);
      toast.error('Listing not found');
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const handleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to save favorites');
      return;
    }
    
    try {
      if (isFavorite) {
        await userAPI.removeFavorite(listing.listing_id);
        toast.success('Removed from favorites');
      } else {
        await userAPI.addFavorite(listing.listing_id);
        toast.success('Added to favorites');
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleSubmitReview = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to leave a review');
      return;
    }
    
    if (reviewRating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (!reviewText.trim()) {
      toast.error('Please write a review');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await reviewsAPI.create({
        listing_id: listing.listing_id,
        rating: reviewRating,
        text: reviewText.trim(),
      });
      toast.success('Review submitted!');
      setReviewText('');
      setReviewRating(0);
      loadListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleClaim = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to claim this listing');
      return;
    }
    
    if (!claimData.business_role) {
      toast.error('Please select your role');
      return;
    }
    
    setSubmittingClaim(true);
    try {
      await listingsAPI.claim(listing.listing_id, claimData);
      toast.success('Claim request submitted! Our team will review it shortly.');
      setShowClaimDialog(false);
      setClaimStep(1);
      setClaimData({
        verification_method: 'admin',
        business_role: '',
        phone_verification: '',
        documentation_url: '',
        additional_notes: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit claim');
    } finally {
      setSubmittingClaim(false);
    }
  };

  const handleSubmitQuestion = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to ask a question');
      return;
    }
    
    if (!questionText.trim()) {
      toast.error('Please enter your question');
      return;
    }
    
    setSubmittingQuestion(true);
    try {
      await questionsAPI.create({
        listing_id: listing.listing_id,
        text: questionText.trim(),
      });
      toast.success('Question submitted!');
      setQuestionText('');
      loadListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit question');
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const handleAnswerQuestion = async (questionId) => {
    if (!answerText.trim()) {
      toast.error('Please enter your answer');
      return;
    }
    
    try {
      await questionsAPI.answer(questionId, { text: answerText.trim() });
      toast.success('Answer submitted!');
      setAnswerText('');
      setAnsweringQuestionId(null);
      loadListing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: listing.name,
        text: `Check out ${listing.name} on MedSearcher`,
        url: window.location.href,
      });
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleCall = () => {
    analyticsAPI.track({ listing_id: listing.listing_id, event_type: 'call' });
    window.open(`tel:${listing.phone}`, '_self');
  };

  const handleDirections = () => {
    analyticsAPI.track({ listing_id: listing.listing_id, event_type: 'direction' });
    const address = encodeURIComponent(`${listing.street}, ${listing.city}, ${listing.state} ${listing.zip_code}`);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
  };

  const isOwner = isAuthenticated && listing?.owner_id === user?.user_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="animate-pulse space-y-6">
            <div className="h-64 bg-gray-200 rounded-xl" />
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) return null;

  const defaultImage = {
    facility: 'https://images.pexels.com/photos/127873/pexels-photo-127873.jpeg',
    specialist: 'https://images.unsplash.com/photo-1645066928295-2506defde470?crop=entropy&cs=srgb&fm=jpg&q=85',
    pharmacy: 'https://images.pexels.com/photos/8657293/pexels-photo-8657293.jpeg',
  };

  const typeColors = {
    facility: 'bg-teal-100 text-teal-700',
    specialist: 'bg-blue-100 text-blue-700',
    pharmacy: 'bg-rose-100 text-rose-700',
  };

  const typeLabels = {
    facility: 'Facility',
    specialist: 'Specialist',
    pharmacy: 'Pharmacy',
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="listing-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-teal-700">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/${listing.listing_type}s`} className="hover:text-teal-700">
              {typeLabels[listing.listing_type]}s
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">{listing.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img
          src={listing.photos?.[0] || defaultImage[listing.listing_type]}
          alt={listing.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-4 left-4 flex gap-2">
          <Badge className={typeColors[listing.listing_type]}>
            {typeLabels[listing.listing_type]}
          </Badge>
          {listing.plan === 'premium' && (
            <Badge className="bg-purple-100 text-purple-700">
              <Award className="w-3 h-3 mr-1" />
              Featured
            </Badge>
          )}
        </div>

        {/* Actions */}
        <div className="absolute top-4 right-4 flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleFavorite}
            className={isFavorite ? 'text-rose-500' : ''}
            data-testid="favorite-btn"
          >
            <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
          </Button>
          <Button variant="secondary" size="sm" onClick={handleShare} data-testid="share-btn">
            <Share2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    {listing.name}
                    {listing.verified && (
                      <BadgeCheck className="w-6 h-6 text-teal-600" />
                    )}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-gray-600">
                    <StarRating 
                      rating={listing.rating_avg || 0} 
                      showValue 
                      showCount 
                      count={listing.review_count || 0}
                      size="lg"
                    />
                  </div>
                </div>
              </div>

              {/* Categories/Specialties */}
              <div className="flex flex-wrap gap-2 mb-4">
                {[...(listing.categories || []), ...(listing.specialties || [])].map((item, i) => (
                  <Badge key={i} variant="secondary">{item}</Badge>
                ))}
              </div>

              {/* Description */}
              {listing.description && (
                <p className="text-gray-600 leading-relaxed">{listing.description}</p>
              )}

              {/* Claim Banner */}
              {!listing.claimed && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium text-amber-800">Is this your listing?</p>
                    <p className="text-sm text-amber-700">Claim it to manage your profile and respond to reviews.</p>
                  </div>
                  <Button 
                    size="sm" 
                    className="bg-amber-600 hover:bg-amber-700 text-white" 
                    onClick={() => setShowClaimDialog(true)}
                    data-testid="claim-btn"
                  >
                    Claim Listing
                  </Button>
                </div>
              )}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="overview" className="bg-white rounded-xl border border-gray-200">
              <TabsList className="w-full justify-start border-b rounded-none px-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="reviews">Reviews ({listing.review_count || 0})</TabsTrigger>
                <TabsTrigger value="qa" data-testid="qa-tab">
                  <HelpCircle className="w-4 h-4 mr-1" />
                  Q&A ({questions.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="p-6">
                {/* Services */}
                {listing.services?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-heading font-semibold text-lg mb-3">Services</h3>
                    <div className="flex flex-wrap gap-2">
                      {listing.services.map((service, i) => (
                        <Badge key={i} variant="outline">{service}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Insurance */}
                {listing.insurance_accepted?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-heading font-semibold text-lg mb-3">Insurance Accepted</h3>
                    <div className="flex flex-wrap gap-2">
                      {listing.insurance_accepted.map((ins, i) => (
                        <Badge key={i} variant="secondary">{ins}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Languages */}
                {listing.languages?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-heading font-semibold text-lg mb-3">Languages</h3>
                    <p className="text-gray-600">{listing.languages.join(', ')}</p>
                  </div>
                )}

                {/* Hours */}
                {listing.hours && (
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-3">Hours</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day) => (
                        <div key={day} className="flex justify-between py-1 border-b border-gray-100">
                          <span className="capitalize text-gray-600">{day}</span>
                          <span className="font-medium">{listing.hours[day] || 'Closed'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="reviews" className="p-6">
                {/* Write Review */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-heading font-semibold text-lg mb-4">Write a Review</h3>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Your rating</p>
                    <StarRating 
                      rating={reviewRating} 
                      interactive 
                      onChange={setReviewRating}
                      size="lg"
                    />
                  </div>
                  <Textarea
                    placeholder="Share your experience..."
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    rows={4}
                    className="mb-4"
                    data-testid="review-text-input"
                  />
                  <Button 
                    onClick={handleSubmitReview}
                    disabled={submittingReview}
                    className="bg-teal-700 hover:bg-teal-800"
                    data-testid="submit-review-btn"
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </div>

                {/* Reviews List */}
                {reviews.length > 0 ? (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.review_id} className="border-b border-gray-100 pb-6">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-gray-900">{review.user_name}</p>
                            <StarRating rating={review.rating} size="sm" />
                          </div>
                          <span className="text-sm text-gray-500">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-600">{review.text}</p>
                        <button
                          onClick={() => {
                            if (!isAuthenticated) {
                              toast.error('Please log in to report reviews');
                              return;
                            }
                            reviewsAPI.flag(review.review_id);
                            toast.success('Review reported');
                          }}
                          className="mt-2 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                        >
                          <Flag className="w-3 h-3" />
                          Report
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No reviews yet. Be the first to review!</p>
                )}
              </TabsContent>

              {/* Q&A Tab */}
              <TabsContent value="qa" className="p-6" data-testid="qa-section">
                {/* Ask Question */}
                <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-heading font-semibold text-lg mb-4">Ask a Question</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Have a question about this provider? Ask and the business owner may respond.
                  </p>
                  <Textarea
                    placeholder="What would you like to know?"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    rows={3}
                    className="mb-4"
                    data-testid="question-input"
                  />
                  <Button 
                    onClick={handleSubmitQuestion}
                    disabled={submittingQuestion}
                    className="bg-teal-700 hover:bg-teal-800"
                    data-testid="submit-question-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submittingQuestion ? 'Submitting...' : 'Submit Question'}
                  </Button>
                </div>

                {/* Questions List */}
                {questions.length > 0 ? (
                  <div className="space-y-6">
                    {questions.map((question) => (
                      <div key={question.question_id} className="border border-gray-200 rounded-lg p-4">
                        {/* Question */}
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                            <HelpCircle className="w-4 h-4 text-teal-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-medium text-gray-900">{question.user_name}</p>
                              <span className="text-xs text-gray-500">
                                {new Date(question.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-gray-700">{question.text}</p>
                          </div>
                        </div>

                        {/* Answer */}
                        {question.answer ? (
                          <div className="ml-11 p-3 bg-teal-50 rounded-lg border-l-4 border-teal-600">
                            <div className="flex items-center gap-2 mb-1">
                              <BadgeCheck className="w-4 h-4 text-teal-600" />
                              <span className="text-sm font-medium text-teal-800">
                                {question.answered_by || 'Business Owner'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {question.answered_at && new Date(question.answered_at).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-gray-700">{question.answer}</p>
                          </div>
                        ) : (
                          <>
                            {isOwner && (
                              <div className="ml-11 mt-3">
                                {answeringQuestionId === question.question_id ? (
                                  <div className="space-y-2">
                                    <Textarea
                                      placeholder="Write your answer..."
                                      value={answerText}
                                      onChange={(e) => setAnswerText(e.target.value)}
                                      rows={2}
                                    />
                                    <div className="flex gap-2">
                                      <Button 
                                        size="sm"
                                        onClick={() => handleAnswerQuestion(question.question_id)}
                                        className="bg-teal-700 hover:bg-teal-800"
                                      >
                                        Submit Answer
                                      </Button>
                                      <Button 
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setAnsweringQuestionId(null);
                                          setAnswerText('');
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <Button 
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setAnsweringQuestionId(question.question_id)}
                                  >
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Answer this question
                                  </Button>
                                )}
                              </div>
                            )}
                            {!isOwner && (
                              <p className="ml-11 mt-2 text-sm text-gray-500 italic">
                                Awaiting response from business
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No questions yet. Be the first to ask!</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 sticky top-24">
              <h2 className="font-heading font-semibold text-lg mb-4">Contact Information</h2>
              
              <div className="space-y-4">
                {/* Address */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-gray-900">{listing.street}</p>
                    <p className="text-gray-600">{listing.city}, {listing.state} {listing.zip_code}</p>
                  </div>
                </div>

                {/* Phone */}
                {listing.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-teal-600 shrink-0" />
                    <a href={`tel:${listing.phone}`} className="text-gray-900 hover:text-teal-700">
                      {listing.phone}
                    </a>
                  </div>
                )}

                {/* Website */}
                {listing.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-teal-600 shrink-0" />
                    <a 
                      href={listing.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-900 hover:text-teal-700 flex items-center gap-1"
                    >
                      Visit Website
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}

                {/* Hours Summary */}
                {listing.hours && (
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-teal-600 shrink-0" />
                    <span className="text-gray-900">
                      {listing.hours.mon === '24/7' ? 'Open 24/7' : 'See hours above'}
                    </span>
                  </div>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="mt-6 space-y-3">
                {listing.phone && (
                  <Button 
                    className="w-full bg-teal-700 hover:bg-teal-800" 
                    onClick={handleCall}
                    data-testid="call-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={handleDirections}
                  data-testid="directions-btn"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Get Directions
                </Button>
                {listing.booking_url && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => {
                      analyticsAPI.track({ listing_id: listing.listing_id, event_type: 'click' });
                      window.open(listing.booking_url, '_blank');
                    }}
                    data-testid="book-btn"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Appointment
                  </Button>
                )}
              </div>
            </div>

            {/* Map */}
            {listing.lat && listing.lng && (
              <ListingMap 
                listings={[listing]} 
                className="h-[250px]"
              />
            )}
          </div>
        </div>

        {/* Nearby Listings */}
        {nearbyListings.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl font-semibold text-gray-900 mb-6">
              Nearby {typeLabels[listing.listing_type]}s
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {nearbyListings.map((l) => (
                <ListingCard key={l.listing_id} listing={l} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Claim Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={(open) => {
        setShowClaimDialog(open);
        if (!open) {
          setClaimStep(1);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {claimStep === 1 && 'Claim This Listing'}
              {claimStep === 2 && 'Verification Details'}
              {claimStep === 3 && 'Confirm & Submit'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  claimStep >= step 
                    ? 'bg-teal-700 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {claimStep > step ? <CheckCircle className="w-4 h-4" /> : step}
                </div>
                {step < 3 && (
                  <div className={`w-12 h-1 ${claimStep > step ? 'bg-teal-700' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>

          <div className="py-4">
            {/* Step 1: Benefits & Role Selection */}
            {claimStep === 1 && (
              <div className="space-y-4">
                <p className="text-gray-600">By claiming this listing, you'll be able to:</p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <ThumbsUp className="w-4 h-4 text-teal-600" />
                    Edit your listing information
                  </li>
                  <li className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-teal-600" />
                    Respond to reviews and questions
                  </li>
                  <li className="flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4 text-teal-600" />
                    Get verified status
                  </li>
                </ul>
                
                <div className="pt-4">
                  <Label className="text-base font-medium">What is your role at this business? *</Label>
                  <Select
                    value={claimData.business_role}
                    onValueChange={(v) => setClaimData({ ...claimData, business_role: v })}
                  >
                    <SelectTrigger className="mt-2" data-testid="role-select">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="administrator">Administrator</SelectItem>
                      <SelectItem value="marketing">Marketing / Communications</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 2: Verification Method */}
            {claimStep === 2 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-medium">Verification Method</Label>
                  <div className="grid grid-cols-1 gap-3 mt-2">
                    {[
                      { id: 'phone', label: 'Phone Verification', desc: 'Verify via a call to the business phone', icon: Phone },
                      { id: 'email', label: 'Email Verification', desc: 'Verify via business domain email', icon: Mail },
                      { id: 'document', label: 'Document Upload', desc: 'Upload business license or ID', icon: FileText },
                      { id: 'admin', label: 'Admin Review', desc: 'Manual verification by our team', icon: User },
                    ].map((method) => (
                      <button
                        key={method.id}
                        onClick={() => setClaimData({ ...claimData, verification_method: method.id })}
                        className={`flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-colors ${
                          claimData.verification_method === method.id
                            ? 'border-teal-700 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <method.icon className={`w-5 h-5 mt-0.5 ${
                          claimData.verification_method === method.id ? 'text-teal-700' : 'text-gray-400'
                        }`} />
                        <div>
                          <p className="font-medium text-gray-900">{method.label}</p>
                          <p className="text-sm text-gray-500">{method.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {claimData.verification_method === 'phone' && (
                  <div>
                    <Label>Phone number to call</Label>
                    <Input
                      placeholder={listing.phone || 'Enter phone number'}
                      value={claimData.phone_verification}
                      onChange={(e) => setClaimData({ ...claimData, phone_verification: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                )}

                {claimData.verification_method === 'document' && (
                  <div>
                    <Label>Documentation URL (Google Drive, Dropbox, etc.)</Label>
                    <Input
                      placeholder="https://..."
                      value={claimData.documentation_url}
                      onChange={(e) => setClaimData({ ...claimData, documentation_url: e.target.value })}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Upload your business license, registration, or ID and paste the share link
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Confirm */}
            {claimStep === 3 && (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Claim Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Listing:</span>
                      <span className="font-medium">{listing.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Your Role:</span>
                      <span className="font-medium capitalize">{claimData.business_role}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Verification:</span>
                      <span className="font-medium capitalize">{claimData.verification_method}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Additional Notes (optional)</Label>
                  <Textarea
                    placeholder="Any additional information to help verify your claim..."
                    value={claimData.additional_notes}
                    onChange={(e) => setClaimData({ ...claimData, additional_notes: e.target.value })}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Our team will review your claim within 1-2 business days. You'll receive an email notification once it's processed.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            {claimStep > 1 && (
              <Button variant="outline" onClick={() => setClaimStep(claimStep - 1)}>
                Back
              </Button>
            )}
            {claimStep < 3 ? (
              <Button 
                onClick={() => setClaimStep(claimStep + 1)}
                disabled={claimStep === 1 && !claimData.business_role}
                className="bg-teal-700 hover:bg-teal-800"
              >
                Continue
              </Button>
            ) : (
              <Button 
                onClick={handleClaim}
                disabled={submittingClaim}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="submit-claim-btn"
              >
                {submittingClaim ? 'Submitting...' : 'Submit Claim'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListingDetail;
